import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import TickerCard from "../components/TickerCard";
import QuoteSection from "../components/QuoteSection";
import { COMPANY, HOME, DISCLAIMER } from "../data/content";
import { AccentHeadline } from "../components/AccentHeadline";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="relative bg-[#14110E] text-white overflow-hidden grain">
        <div className="absolute inset-0 hero-grid opacity-60" aria-hidden />
        <div className="absolute -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-[#F26A1B]/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-28 lg:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 eyebrow text-[#F26A1B]" data-testid="hero-eyebrow">
                <span className="rule-orange" />
                {HOME.eyebrow}
              </div>
              <h1
                className="font-display font-semibold mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight"
                data-testid="hero-headline"
              >
                <AccentHeadline text={HOME.headline} accent={HOME.headlineAccent} />
              </h1>
              <p className="mt-8 text-[17px] lg:text-lg text-[#F5F2EB] max-w-2xl leading-relaxed">
                {HOME.intro}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/investors"
                  className="group inline-flex items-center justify-center gap-2 btn-gradient rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors"
                  data-testid="hero-cta-investors"
                >
                  Investor Relations
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/sec-filings"
                  className="group inline-flex items-center justify-center gap-2 border border-[#4A3D30] hover:border-white hover:bg-white/5 text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors"
                  data-testid="hero-cta-filings"
                >
                  SEC Filings
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 lg:pt-6">
              <TickerCard testId="hero-ticker-card" />
              <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-[#8A8075]">
                Formerly {COMPANY.formerName} · Rebrand {COMPANY.rebrandDate}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live quote (self-hosted, Stooq-backed) */}
      <QuoteSection />

      {/* Approach */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="section-approach">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> {HOME.approachEyebrow}
            </div>
            <h2 className="mt-6 font-display text-3xl lg:text-[44px] leading-[1.05] text-[#14110E] font-semibold tracking-tight">
              {HOME.approachTitle}
            </h2>
            <p className="mt-6 text-[#4A3D30] leading-relaxed">{HOME.approachIntro}</p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOME.pillars.map((p) => (
              <div
                key={p.num}
                className="border border-[#DED6C6] p-7 hover:border-[#14110E] transition-colors group"
                data-testid={`pillar-${p.num}`}
              >
                <div className="font-mono text-xs tracking-[0.22em] text-[#F26A1B]">{p.num}</div>
                <h3 className="mt-4 font-display text-xl text-[#14110E] font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm text-[#4A3D30] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities strip */}
      <section className="bg-[#F4F1EA] border-y border-[#DED6C6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
                <span className="rule-orange" /> Acquisition-Oriented Growth
              </div>
              <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold leading-tight tracking-tight">
                Built to identify opportunity and execute with discipline
              </h2>
              <p className="mt-6 text-[#4A3D30] leading-relaxed">
                Trafalgar International, Inc. is positioning itself as a flexible public-company platform capable
                of evaluating opportunities across industries, transaction structures, and stages of development.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-[#DED6C6] border-y border-[#DED6C6]">
                {HOME.opportunities.map((o, i) => (
                  <li key={o} className="flex items-center gap-6 py-5" data-testid={`opportunity-${i}`}>
                    <span className="font-mono text-xs text-[#A79E93] w-10">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#14110E] font-medium">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cards grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> Explore
          </div>
          <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold leading-tight tracking-tight">
            Follow Trafalgar International's next phase of growth
          </h2>
          <p className="mt-5 text-[#4A3D30] leading-relaxed">
            Review market information, SEC filings, corporate profile and investor materials.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {HOME.cards.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`home-card-${c.key}`}
              className="group block border border-[#DED6C6] hover:border-[#14110E] p-8 min-h-[220px] transition-colors relative"
            >
              <h3 className="font-display text-2xl text-[#14110E] font-semibold pr-8">{c.title}</h3>
              <p className="mt-4 text-sm text-[#4A3D30] leading-relaxed">{c.body}</p>
              <ArrowUpRight className="absolute bottom-8 right-8 h-6 w-6 text-[#14110E] group-hover:text-[#F26A1B] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {/* Regulatory pane */}
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
                <span className="rule-orange" /> Public Reporting Company
              </div>
              <h3 className="mt-5 font-display text-2xl lg:text-3xl font-semibold">
                Trafalgar International files with the U.S. Securities and Exchange Commission.
              </h3>
              <p className="mt-5 text-[#F5F2EB] text-sm leading-relaxed max-w-2xl">
                {DISCLAIMER.forwardLooking}
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <a
                href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001695473&type=&dateb=&owner=include&count=40"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white hover:bg-white hover:text-[#14110E] text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors"
                data-testid="home-edgar-cta"
              >
                Open SEC EDGAR profile <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
